import { useState } from "react";
import type { BillingPlan, OnboardingPricing } from "../lib/api";
import { formatMoney, planName, recurringLabel } from "../lib/billingPlan";

interface Props {
  pricing: OnboardingPricing;
  /** Previously chosen plan when the user comes back to change it. */
  initialPlan: BillingPlan | null;
  onSelected: (plan: BillingPlan) => void;
}

const PLANS: BillingPlan[] = ["weekly", "monthly"];

export function PlanStep({ pricing, initialPlan, onSelected }: Props) {
  const [plan, setPlan] = useState<BillingPlan | null>(initialPlan);
  const dueToday = formatMoney(pricing.totalDueTodayCents, pricing.currency);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-900">
          Choose your plan
        </h2>
        <p className="text-sm text-slate-600 mt-1">
          Both plans start the same way: {dueToday} today for your first month
          ({formatMoney(pricing.firstMonthCents, pricing.currency)}) plus the
          one-time activation fee (
          {formatMoney(pricing.activationFeeCents, pricing.currency)}). Your plan
          sets how you're billed after the first {pricing.trialDays} days.
        </p>
      </div>

      <div
        role="radiogroup"
        aria-label="Billing plan"
        className="grid grid-cols-1 sm:grid-cols-2 gap-3"
      >
        {PLANS.map((p) => {
          const selected = plan === p;
          return (
            <button
              key={p}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => setPlan(p)}
              className={
                "text-left rounded-xl border bg-white p-5 shadow-sm transition " +
                (selected
                  ? "border-brand-500 ring-2 ring-brand-100"
                  : "border-slate-200 hover:border-slate-300")
              }
            >
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm font-semibold text-slate-900">
                  {planName(p)}
                </span>
                <span
                  aria-hidden
                  className={
                    "h-4 w-4 rounded-full border " +
                    (selected
                      ? "border-brand-500 bg-brand-500 ring-2 ring-inset ring-white"
                      : "border-slate-300")
                  }
                />
              </div>
              <div className="mt-2 text-2xl font-semibold text-slate-900">
                {recurringLabel(p, pricing)}
              </div>
              <p className="mt-1 text-xs text-slate-500">
                Charged automatically every {p === "monthly" ? "month" : "week"},
                starting {pricing.trialDays} days after enrollment.
              </p>
            </button>
          );
        })}
      </div>

      <button
        type="button"
        disabled={!plan}
        onClick={() => plan && onSelected(plan)}
        className="w-full rounded-lg bg-brand-500 px-4 py-3 text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-brand-600 transition"
      >
        {plan ? `Continue with ${planName(plan)}` : "Select a plan to continue"}
      </button>
    </div>
  );
}

/**
 * Compact plan bar shown above the Sign step. Switching here keeps the
 * Sign step mounted so nothing the client typed is lost.
 */
export function SelectedPlanBar({
  plan,
  pricing,
  onChange,
}: {
  plan: BillingPlan;
  pricing: OnboardingPricing;
  onChange: (plan: BillingPlan) => void;
}) {
  const other: BillingPlan = plan === "monthly" ? "weekly" : "monthly";
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm shadow-sm">
      <span className="text-slate-700">
        Plan:{" "}
        <span className="font-semibold text-slate-900">
          {planName(plan)} · {recurringLabel(plan, pricing)}
        </span>
      </span>
      <button
        type="button"
        onClick={() => onChange(other)}
        className="shrink-0 text-xs font-medium text-brand-600 hover:text-brand-700 underline underline-offset-2"
      >
        Switch to {planName(other).toLowerCase()}
      </button>
    </div>
  );
}
